/**
 * PC(alt-tab) 오퍼레이터 쿠폰 도구 (개편 v0.3)
 * ─ 기획서 2026-07-03-pc-coupon-deliver-by-phone-design.md
 * ─ 윈도우 POS PC 에서 브라우저 앱모드로 alt-tab 사용. 데스크톱 가로 + 키보드 우선.
 * ─ 은행과 같은 좌측 업무 탭 / 가족 선택 / 쿠폰 목록. 전화번호는 검색 팝업에서 입력.
 * ─ 사용·만료예외·발급 API 계약 유지. 취소는 /visit/cancel 자동원복 위임.
 * ─ 은행 셸·가족·전화 패드 스타일 재사용, 쿠폰 전용 스타일은 pco- 범위.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ConfigProvider, Modal, Tabs, theme as antdTheme } from 'antd';
import koKR from 'antd/locale/ko_KR';
import { ArrowLeftOutlined, PhoneFilled, SearchOutlined, ReloadOutlined, HistoryOutlined, GiftOutlined, TagsOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import PhoneNumpad from '../components/PhoneNumpad';
import {
  searchMemberCoupons,
  redeemCoupon,
  listTodayRedemptions,
  getMemberLookup,
  getIssuableCoupons,
  issueCoupon,
} from '../services/api';
import './AllInOnePage.css';
import './BankWorkspace.css';
import './PcCouponOpsPage.css';
import '../components/HistoryPanel.css';

function genRid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `tools-${crypto.randomUUID()}`;
  }
  return `tools-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function formatWon(n) {
  if (n === null || n === undefined) return '-';
  return `${Number(n).toLocaleString('ko-KR')}원`;
}

function fmtPhone(digits) {
  return digits.length === 8 ? `010-${digits.slice(0, 4)}-${digits.slice(4)}` : null;
}

function errMsg(e) {
  return e?.response?.data?.message || e?.response?.data?.error_code || e?.message || '오류';
}

/**
 * 미가입 번호 안내 문구 (기획서 v0.4 §2 D7 · §4.2).
 *
 * 쿠폰은 앱으로 전달되고 앱은 가입자만 쓰므로 미가입 번호에는 발행할 수 없다.
 * 발급 자체는 이미 막혀 있지만(`openIssue` 는 selectedIssueMember 필요) 5초 배너로만
 * 알려 오퍼레이터가 **이유와 다음 행동**을 모른 채 지나쳤다 → 모달로 격상.
 *
 * ⚠️ **테스트가 이 상수를 import 해 단언한다**(`PcCouponOpsPage.noMemberPopup.test.jsx`).
 *    JSX 에 문구를 직접 박으면 문구를 바꿔도 테스트가 따라오지 않는다.
 */
export const NO_MEMBER_NOTICE =
  '쿠폰은 앱으로 전달되므로 고객님께 앱 설치 후 가입을 안내해 주세요.';

export default function PcCouponOpsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [banner, setBanner] = useState(null); // {kind:'ok'|'err', text}
  const [phoneDigits, setPhoneDigits] = useState(''); // 010 뒤 8자리 (PhoneNumpad)
  const [members, setMembers] = useState(null);
  const [selectedRedeemMemberId, setSelectedRedeemMemberId] = useState(null);
  const [searchedRedeemPhone, setSearchedRedeemPhone] = useState(null);
  const [today, setToday] = useState([]);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const historyPending = useRef(false);
  const [activeTool, setActiveTool] = useState('redeem');
  const [phoneSearchOpen, setPhoneSearchOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const searchPending = useRef(false);
  const confirmPending = useRef(false);

  // 구획 C — 즉석 발급
  const [issueMembers, setIssueMembers] = useState(null);
  const [selectedIssueMember, setSelectedIssueMember] = useState(null);
  const [issuable, setIssuable] = useState([]);

  const refreshToday = useCallback(async () => {
    if (historyPending.current) return;
    historyPending.current = true;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const res = await listTodayRedemptions();
      setToday(res?.items || []);
    } catch (e) {
      setHistoryError(errMsg(e));
    } finally {
      historyPending.current = false;
      setHistoryLoading(false);
    }
  }, []);

  const loadIssuable = useCallback(async () => {
    try {
      const res = await getIssuableCoupons();
      setIssuable(res?.coupons || []);
    } catch { /* 발급가능 목록 조회 실패는 무시 */ }
  }, []);

  useEffect(() => { refreshToday(); loadIssuable(); }, [refreshToday, loadIssuable]);

  const flash = (kind, text) => {
    setBanner({ kind, text });
    window.setTimeout(() => setBanner(null), 5000);
  };

  // ── 구획① 전화 검색 → 사용처리/만료예외 ──
  const onSearchPhone = useCallback(async () => {
    if (phoneDigits.length !== 8 || searchPending.current) return;
    searchPending.current = true;
    setSearching(true);
    setSearchError(null);
    const fullPhone = `010${phoneDigits}`;
    setMembers(null);
    setSelectedRedeemMemberId(null);
    try {
      const res = await searchMemberCoupons(fullPhone);
      const nextMembers = res?.members || [];
      setMembers(nextMembers);
      setSelectedRedeemMemberId(nextMembers[0]?.member_id || null);
      setSearchedRedeemPhone(fullPhone);
      setPhoneDigits('');
      setPhoneSearchOpen(false);
      if (!nextMembers.length) flash('err', '해당 전화번호의 회원/쿠폰이 없습니다.');
    } catch (e) {
      setSearchError(errMsg(e));
    } finally {
      searchPending.current = false;
      setSearching(false);
    }
  }, [phoneDigits]);

  const openRedeem = (coupon, member) => setModal({
    type: coupon.expired_exception_eligible ? 'expired-exception' : 'redeem',
    title: coupon.expired_exception_eligible ? '만료예외 수동 차감' : '쿠폰 사용 처리',
    warn: coupon.expired_exception_eligible
      ? '만료된 쿠폰을 예외 처리합니다. 취소해도 복원되지 않습니다.' : '',
    coupon, member,
    fields: { amount: '', reason: '' },
  });

  // ── 구획③ 즉석 발급 ──
  const onLookupIssueMember = useCallback(async () => {
    if (phoneDigits.length !== 8 || searchPending.current) return;
    searchPending.current = true;
    setSearching(true);
    setSearchError(null);
    const fullPhone = `010${phoneDigits}`;
    setIssueMembers(null); setSelectedIssueMember(null);
    try {
      const res = await getMemberLookup(fullPhone);
      const ms = res?.members || [];
      setIssueMembers(ms);
      if (ms.length === 1) setSelectedIssueMember(ms[0]);
      setPhoneDigits('');
      setPhoneSearchOpen(false);
      // 미가입(0명) — 배너 대신 **안내 모달**(D7). 배너는 5초 뒤 사라져 놓치기 쉬웠다.
      // 🔴 배너를 함께 띄우지 않는다 — 같은 사실을 두 번 알리는 중복이다.
      //    회귀 가드: noMemberPopup.test.jsx 케이스 5.
      // ⚠️ WITHDRAWN·INACTIVE 회원도 여기로 떨어진다(BE 가 status='ACTIVE' 로 거름 —
      //    tools_coupon_ops.py:144). 문구를 "가입된 회원이 없습니다"로 두어 셋을 포괄한다.
      if (ms.length === 0) {
        setModal({
          type: 'no-member',
          title: '미가입 번호입니다',
          phone: fullPhone,
        });
      }
    } catch (e) {
      setSearchError(errMsg(e));
    } finally {
      searchPending.current = false;
      setSearching(false);
    }
  }, [phoneDigits]);

  const openIssue = (coupon) => setModal({
    type: 'issue',
    title: '즉석 쿠폰 발급',
    warn: '고객에게 쿠폰을 즉석 발급합니다(값 지급). 발급 내역은 기록·알림됩니다.',
    coupon, member: selectedIssueMember,
    fields: { reason: '' },
  });

  const confirmModal = useCallback(async () => {
    if (!modal || confirmPending.current) return;
    confirmPending.current = true;
    setBusy(true);
    const rid = genRid();
    try {
      if (modal.type === 'redeem' || modal.type === 'expired-exception') {
        const f = modal.fields;
        const isExc = modal.type === 'expired-exception';
        if (isExc && !f.reason.trim()) throw new Error('만료예외는 사유가 필수입니다');
        await redeemCoupon({
          couponIssueId: modal.coupon.coupon_issue_id, toolsRequestId: rid,
          confirmedDiscountAmount: f.amount === '' ? null : Number(f.amount),
          allowExpired: isExc, reason: isExc ? f.reason.trim() : null,
        });
        flash('ok', isExc ? '만료예외 차감 완료' : '사용 처리 완료');
        if (members && searchedRedeemPhone) {
          const refreshed = await searchMemberCoupons(searchedRedeemPhone);
          const nextMembers = refreshed?.members || [];
          setMembers(nextMembers);
          setSelectedRedeemMemberId((current) => (
            nextMembers.some((member) => member.member_id === current)
              ? current
              : (nextMembers[0]?.member_id || null)
          ));
        }
      } else if (modal.type === 'issue') {
        if (!modal.fields.reason.trim()) throw new Error('발급 사유는 필수입니다');
        const res = await issueCoupon({
          couponDefId: modal.coupon.coupon_def_id,
          memberId: modal.member.member_id,
          reason: modal.fields.reason.trim(),
        });
        flash('ok', `발급 완료: ${res.coupon_name} → ${modal.member.nickname || modal.member.member_id}`);
      }
      setModal(null);
      await refreshToday();
    } catch (e) {
      flash('err', errMsg(e));
    } finally {
      confirmPending.current = false;
      setBusy(false);
    }
  }, [modal, members, searchedRedeemPhone, refreshToday]);

  const setField = (k, v) => setModal((m) => ({ ...m, fields: { ...m.fields, [k]: v } }));

  const openPhoneSearch = () => {
    setPhoneDigits('');
    setSearchError(null);
    setPhoneSearchOpen(true);
  };
  const isRedeem = activeTool === 'redeem';
  const currentMembers = isRedeem ? members : issueMembers;
  const selectedMember = isRedeem
    ? members?.find((member) => member.member_id === selectedRedeemMemberId)
    : selectedIssueMember;
  const hasMembers = Boolean(currentMembers?.length);
  const storeName = localStorage.getItem('store_name') || user?.store_name;

  const workspace = (
    <div className="tab-pane">
      <div className="zb-desk pco-desk">
        {!hasMembers ? (
          <div className="bank-waiting pco-waiting">
            <div className="bank-waiting-notice">
              <span className="pco-waiting-icon" aria-hidden="true"><PhoneFilled /></span>
              <div className="bank-waiting-copy">
                <h2>전화번호로 회원을 검색해 주세요</h2>
                {currentMembers?.length === 0 && <p>해당 전화번호의 회원 없음</p>}
                <div className="bank-waiting-search">
                  <Button icon={<SearchOutlined />} onClick={openPhoneSearch} disabled={busy || searching} data-testid="pco-open-phone-search">전화번호 검색</Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="zb-overview pco-redeem-results">
            <aside className="zb-family-column">
              <span className="zb-caption">가족 선택</span>
              <div className="zb-family-search">
                <Button icon={<ReloadOutlined />} onClick={openPhoneSearch} disabled={busy || searching} data-testid="pco-open-phone-search">다른 회원 검색</Button>
              </div>
              <div className="zb-family pco-redeem-member-pick" role="group" aria-label={isRedeem ? '쿠폰 회원 선택' : '쿠폰 발급 회원 선택'}>
                {currentMembers.map((member) => {
                  const selected = selectedMember?.member_id === member.member_id;
                  return (
                    <button key={member.member_id} type="button" className="zb-person pco-redeem-member"
                      aria-pressed={selected} disabled={busy || searching}
                      data-testid={'pco-' + activeTool + '-member-' + member.member_id}
                      onClick={() => isRedeem ? setSelectedRedeemMemberId(member.member_id) : setSelectedIssueMember(member)}>
                      <span className="zb-person-copy"><strong>{member.nickname || member.member_id}</strong></span>
                      {isRedeem && <small className="zb-family-role">{(member.coupons || []).length}장</small>}
                    </button>
                  );
                })}
              </div>
            </aside>
            <section className={'pco-member-coupons' + (isRedeem ? '' : ' pco-issue-coupons')} data-testid="pco-selected-member-coupons">
              <div className="pco-coupon-list-head">
                <h2>{isRedeem ? '쿠폰 목록' : '발급 쿠폰 목록'}</h2>
                {selectedMember && <span className="pco-list-count">{isRedeem ? (selectedMember.coupons || []).length : issuable.length}장</span>}
              </div>
              <p className="pco-desc">{isRedeem
                ? '결제 후 사용할 쿠폰을 선택해 주세요. 만료예외 처리는 사유가 필요합니다.'
                : '현재 지점에서 사용할 쿠폰을 선택해 주세요. 발급 전 대상과 사유를 확인합니다.'}</p>
              {!selectedMember ? <div className="pco-note">쿠폰을 발급할 가족 구성원을 선택해 주세요.</div> : (
                <>
                  {(isRedeem ? (selectedMember.coupons || []).length : issuable.length) === 0 && (
                    <div className="pco-note">{isRedeem ? '사용 가능한 쿠폰이 없습니다.' : '발급 가능 쿠폰 없음'}</div>
                  )}
                  <div className="pco-coupon-list">
                    {isRedeem ? (selectedMember.coupons || []).map((coupon) => (
                      <article key={coupon.coupon_issue_id} className={'pco-coupon' + (coupon.expired ? ' pco-coupon-exp' : '')}>
                        <div className="pco-coupon-top"><span>{coupon.expired ? '만료' : '보유'}</span></div>
                        <div className="pco-coupon-info">
                          <b>{coupon.coupon_name}</b>
                        </div>
                        <div className="pco-coupon-meta">
                          <span className="pco-period-label">사용기한</span>
                          <span className="pco-period-value">{coupon.expires_at ? '~ ' + coupon.expires_at.slice(0, 10).replace(/-/g, '.') : '정보 없음'}</span>
                        </div>
                        <div className="pco-coupon-action">
                          <button type="button" className={'pco-btn pco-btn-sm' + (coupon.expired_exception_eligible ? ' pco-btn-exception' : ' pco-btn-redeem')}
                            disabled={busy || searching || (coupon.expired && !coupon.expired_exception_eligible)} onClick={() => openRedeem(coupon, selectedMember)}>
                            {coupon.expired_exception_eligible ? '만료예외 처리' : (coupon.expired ? '만료됨' : '사용 처리')}
                          </button>
                        </div>
                      </article>
                    )) : issuable.map((coupon) => (
                      <article key={coupon.coupon_def_id} className="pco-coupon">
                        <div className="pco-coupon-top"><span>{coupon.all_store ? '전 지점' : '현재 지점'}</span></div>
                        <div className="pco-coupon-info"><b>{coupon.name}</b></div><div className="pco-coupon-meta"><span className="pco-period-label">사용 지점</span><span className="pco-period-value">{coupon.all_store ? '전 지점' : '현재 지점'}</span></div>
                        <div className="pco-coupon-action"><button type="button" className="pco-btn pco-btn-sm pco-btn-redeem" disabled={busy || searching} onClick={() => openIssue(coupon)}>발급</button></div>
                      </article>
                    ))}
                  </div>
                </>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <ConfigProvider locale={koKR} theme={{ algorithm: antdTheme.defaultAlgorithm, token: {
      colorPrimary: '#007800', colorBgContainer: '#ffffff', colorBgElevated: '#ffffff',
      colorBgLayout: '#f5f5f5', colorText: '#0d0d0d', colorTextSecondary: '#666666',
      colorBorder: '#dcdcdc', colorSuccess: '#189018', borderRadius: 6,
      fontFamily: '"Noto Sans KR", sans-serif',
    } }}>
    <div className="bank-workspace-page pco-page">
      <header className="all-header">
        <div className="all-header-left">
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/')} disabled={busy || searching}>메뉴</Button>
          <span className="all-header-title">쿠폰도구</span>
        </div>
        <div className="all-header-right">
          <Button icon={<HistoryOutlined />} onClick={() => setHistoryOpen(true)} disabled={busy || searching}>오늘 처리 내역</Button>
          {storeName && <span className="bank-store-badge"><i aria-hidden="true" />{storeName}</span>}
          <span className="all-user-name"><small>ID</small><strong>{user?.name || user?.username || '운영자'}</strong></span>
        </div>
      </header>
      {banner && <div role="status" className={'pco-banner pco-banner-' + banner.kind}>{banner.text}</div>}
      <main className="all-main">
        <div className="zb-shell-workspace">
          <Tabs className="zb-category-tabs" tabPosition="left" activeKey={activeTool} animated={false}
            onChange={(key) => { if (!searchPending.current && !busy) setActiveTool(key); }}
            items={[
              { key: 'redeem', disabled: busy || searching, label: <span data-testid="pco-tab-redeem"><TagsOutlined />쿠폰 사용</span>, children: isRedeem ? workspace : null },
              { key: 'issue', disabled: busy || searching, label: <span data-testid="pco-tab-issue"><GiftOutlined />쿠폰 발급</span>, children: !isRedeem ? workspace : null },
            ]} />
        </div>
      </main>
      <Modal open={phoneSearchOpen} onCancel={() => { if (!searchPending.current) setPhoneSearchOpen(false); }}
        footer={null} title="전화번호로 회원 검색" centered width={520} destroyOnHidden
        className="bank-phone-modal" closable={!searching} maskClosable={!searching} keyboard={!searching}>
        <PhoneNumpad digits={phoneDigits} onChange={setPhoneDigits}
          onSubmit={isRedeem ? onSearchPhone : onLookupIssueMember} disabled={busy || searching}
          submitLabel={searching ? '조회 중…' : '조회'} errorText={searchError} testIdPrefix="pco-pad" />
      </Modal>

      {/* 오늘 처리 내역은 보조 정보이므로 메인 작업대에서 분리해 필요할 때만 연다. */}
      {historyOpen && (
        <div className="pco-history-backdrop" onClick={() => setHistoryOpen(false)}>
          <aside className="pco-history-panel" role="dialog" aria-modal="true" aria-labelledby="pco-history-title" onClick={(e) => e.stopPropagation()}>
            <div className="pco-history-head tools-history-head">
              <h2 id="pco-history-title">오늘 처리 내역</h2>
              <div className="tools-history-header-actions">
                <Button className="tools-history-refresh" icon={<ReloadOutlined />} disabled={historyLoading} onClick={refreshToday}>새로고침</Button>
                <Button className="tools-history-close" onClick={() => setHistoryOpen(false)}>닫기</Button>
              </div>
            </div>
            <div className="tools-history-tools">
              <div className="tools-history-summary-cards" aria-label="쿠폰 처리 현황">
                <div className="tools-history-summary-card" data-testid="pco-history-used"><span>사용 완료</span><strong>{historyLoading || historyError ? '—' : today.filter(t => t.state === 'active').length}<small>건</small></strong></div>
                <div className="tools-history-summary-card is-cancel" data-testid="pco-history-cancelled"><span>사용 취소</span><strong>{historyLoading || historyError ? '—' : today.filter(t => t.state === 'refunded').length}<small>건</small></strong></div>
              </div>
            </div>
            {historyError && <div className="pco-history-error" role="alert">{historyError}</div>}
            {historyLoading && <p role="status">내역을 불러오는 중…</p>}
            <div className="pco-today">
              {today.length === 0 && !historyLoading && !historyError && <div className="pco-history-empty">오늘 처리 내역이 없습니다.</div>}
              {today.length > 0 && <table className="pco-history-table">
                <thead><tr><th>상태</th><th>대상</th><th>쿠폰명</th><th>차감액</th><th>처리 시간</th></tr></thead>
                <tbody>{today.map((t, i) => (
                  <tr key={t.redemption_id || i}>
                    <td><span className={`pco-history-status ${t.state === 'refunded' ? 'is-cancel' : ''}`}>{t.state === 'refunded' ? '취소됨' : '사용 완료'}</span>
                      {t.state !== 'refunded' && (t.redeem_source === 'tools_force' || t.expired_exception) && <small className="pco-history-source">{t.redeem_source === 'tools_force' ? '강제' : '만료예외'}</small>}
                    </td>
                    <td>{t.nickname || '(별명 없음)'}</td>
                    <td className="pco-history-coupon">{t.coupon_name || t.coupon_issue_id}</td>
                    <td className="pco-history-amount">{formatWon(t.discount_amount)}</td>
                    <td className="pco-history-time">{t.redeemed_at || '—'}</td>
                  </tr>
                ))}</tbody>
              </table>}
            </div>
          </aside>
        </div>
      )}

      {/* 확인 모달 */}
      {modal && (
        <div className="pco-modal-backdrop" onClick={() => !busy && setModal(null)}>
          <div className={`pco-modal${modal.warn ? ' pco-modal-warn' : ''}`} onClick={(e) => e.stopPropagation()}>
            <h3>{modal.title}</h3>
            {modal.warn && <p className="pco-modal-warntext">{modal.warn}</p>}

            {(modal.type === 'redeem' || modal.type === 'expired-exception') && (
              <>
                <label>대상</label>
                <div className="pco-modal-ro">{modal.coupon?.coupon_name} · {modal.member?.nickname}</div>
                <label>차감액(자동계산 불가 시 입력, 원)</label>
                <input className="pco-input" type="number" value={modal.fields.amount}
                  onChange={(e) => setField('amount', e.target.value)} />
              </>
            )}
            {modal.type === 'issue' && (
              <>
                <label>대상 회원</label>
                <div className="pco-modal-ro">{modal.member?.nickname || modal.member?.member_id}</div>
                <label>발급 쿠폰</label>
                <div className="pco-modal-ro">{modal.coupon?.name} {modal.coupon?.all_store ? '(전지점)' : ''}</div>
              </>
            )}

            {modal.type === 'no-member' && (
              <>
                <div className="pco-modal-ro" data-testid="pco-no-member-phone">
                  {fmtPhone(modal.phone?.slice(3) || '') || modal.phone}
                </div>
                <p className="pco-modal-warntext">이 번호로 가입된 회원이 없습니다.</p>
                <p>{NO_MEMBER_NOTICE}</p>
              </>
            )}

            {/* 🔴 사유는 처리계열(redeem/expired-exception/issue) 전용이다.
                no-member 는 알림일 뿐이라 입력이 없어야 한다 — 가드를 빼면 빈 사유 칸이 뜬다.
                회귀 가드: noMemberPopup.test.jsx 케이스 2. */}
            {modal.type !== 'no-member' && (
              <>
                <label>사유{modal.type === 'redeem' ? '(선택)' : '(필수)'}</label>
                <input className="pco-input" value={modal.fields.reason} autoFocus={modal.type === 'issue'}
                  onChange={(e) => setField('reason', e.target.value)} />
              </>
            )}

            {/* no-member 는 되돌릴 동작이 없어 '취소'가 의미를 갖지 않는다 → 확인 1개.
                회귀 가드: noMemberPopup.test.jsx 케이스 3. */}
            <div className="pco-modal-actions">
              {modal.type === 'no-member' ? (
                <button className="pco-btn pco-btn-primary" onClick={() => setModal(null)}>확인</button>
              ) : (
                <>
                  <button className="pco-btn" onClick={() => setModal(null)} disabled={busy}>취소</button>
                  <button className="pco-btn pco-btn-primary" onClick={confirmModal} disabled={busy}>
                    {busy ? '처리 중…' : '확인'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </ConfigProvider>
  );
}
